import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import {
    FORM_ID,
    FORMATTED_PHONE_NUMBER_LEN,
    API_URL_CHECK_PHONE_NUMBER_TRANSFER_OWNERSHIP,
    API_URL_REJECT_CONSENT,
    API_URL_REJECT_PORTIN_TRANSFER_OWNERSHIP_ORDER,
    API_URL_REJECT_TRANSFER_OWNERSHIP_ORDER_TO_B2B,
    API_URL_SEND_SMS,
    API_URL_SET_KEEP_AGREEMENT,
    API_URL_SET_OFFERING_CODE,
    API_URL_SET_TRANSFER_DATE,
    API_URL_SET_TRANSFER_OWNER_PERSONAL_DETAILS,
    API_URL_SMS_OR_EMAIL,
    API_URL_SUBMIT_CONSENT,
    API_URL_SUBMIT_PORTIN_TRANSFER_OWNERSHIP_ORDER,
    API_URL_SUBMIT_TRANSFER_OWNERSHIP_ORDER,
    API_URL_SUBMIT_TRANSFER_OWNERSHIP_ORDER_TO_B2B,
    API_URL_UPDATE_ORG_CONSENT_TO_B2B,
    API_URL_CONSENT_TO_B2B_BANKID_RESET,
    API_URL_REJECT_TRANSFER_OWNERSHIP_ORDER,
    API_URL_FORMER_LEGAL_CONSENT_REJECT,
    API_URL_FORMER_LEGAL_CONSENT_APPROVE,
    API_URL_FORMER_LEGAL_CONSENT_SEND_CODE,
    API_URL_FORMER_LEGAL_CONSENT_VERIFY_CODE,
    API_URL_UPDATE_FORMER_LEGAL_B2B,
} from "../../../../../constants";
import { OfferingCode, RootState } from "../../../../../models";
import * as Ajax from "../../../app/Ajax";
import * as generalActions from "../../../app/state/generalActions";
import { actionTypes as RF_AT } from "../../../rapidform";
import { allValidationRules, maskField, VALIDATION_FIELD } from "../../../rapidform/formValidation";
import TEXT from "../../text-consts";
import { isPhoneNumberUsable } from "../../utils";
import * as AT from "../actionTypes";
import { getFormById } from "../selectors/formSelectors";

///////////////////
// Existing flow //
///////////////////
export const setSelectedPhoneNumber =
    (selectedPhoneNumber: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.TRANSFER_OWNERSHIP),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.TRANSFER_OWNERSHIP,
                inputName: `transfer-number`,
                inputValue: selectedPhoneNumber,
                visible: false,
            },
        });
    };

export const checkTransferOwnerPhoneNumber =
    (
        phoneNumber: string,
        overrideFirstName?: string,
        overrideLastName?: string,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const onlyDigits = (phoneNumber || "").replace(/\s/g, "");

        if (!isPhoneNumberUsable(onlyDigits)) {
            return dispatch(generalActions.setNegativeFlash(TEXT.ERROR.INVALID_PHONE_NUMBER_FORMAT));
        }

        return dispatch({
            type: AT.CHECKOUT_CHECK_EXISTING_PHONE_NUMBER_TRANSFER_OWNERSHIP,
            payload: Ajax.post(
                {
                    url: API_URL_CHECK_PHONE_NUMBER_TRANSFER_OWNERSHIP,
                    data: {
                        phoneNumber: onlyDigits,
                        overrideFirstName,
                        overrideLastName,
                    },
                },
                dispatch,
            ),
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.CHECK_PHONE_NUMBER,
                },
            },
        });
    };

export const chooseTransferOwnerPhoneNumber =
    (
        newPhoneNumber: string,
        oldPhoneNumber: string,
        shouldLookUp = true,
        overrideFirstName?: string,
        overrideLastName?: string,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const validationRule = allValidationRules[VALIDATION_FIELD.SUBSCRIPTION_NUMBER];

        const maskedNumber = maskField((newPhoneNumber || "").replace(/\s/g, ""), oldPhoneNumber, validationRule);

        dispatch(setSelectedPhoneNumber(maskedNumber));

        // Maybe check phone number
        if (shouldLookUp && maskedNumber.length >= FORMATTED_PHONE_NUMBER_LEN && maskedNumber !== oldPhoneNumber) {
            return dispatch(checkTransferOwnerPhoneNumber(maskedNumber, overrideFirstName, overrideLastName));
        }
    };

export const setCanSendTransferOrder =
    (value: boolean): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        return dispatch({
            type: AT.CHECKOUT_SET_CAN_SEND_TRANSFER_ORDER,
            payload: {
                canSendOrder: value,
            },
        });
    };

export const setTransferOwnerPersonalDetails =
    (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch, getState) => {
        const { values } = getFormById(getState(), FORM_ID.TRANSFER_OWNERSHIP);
        const email = values[VALIDATION_FIELD.EMAIL];
        const phoneNumber = values[VALIDATION_FIELD.PHONE];
        const personalDetailsContainer = {
            email,
            phoneNumber,
        };

        const response = Ajax.post(
            {
                url: API_URL_SET_TRANSFER_OWNER_PERSONAL_DETAILS,
                data: personalDetailsContainer,
            },
            dispatch,
        ).then(() => ({
            success: true,
            message: null,
        }));

        return dispatch({
            type: AT.CHECKOUT_SET_TRANSFER_OWNER_PERSONAL_DETAILS,
            payload: response,
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.ORDER_SUBMIT,
                },
            },
        });
    };

export const submitTransferOwnershipOrder = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) =>
    dispatch({
        type: AT.CHECKOUT_SUBMIT_TRANSFER_OWNERSHIP_ORDER,
        payload: Ajax.post(
            {
                url: API_URL_SUBMIT_TRANSFER_OWNERSHIP_ORDER,
            },
            dispatch,
        ),
    });

export const rejectTransferOwnershipOrder =
    (reasonText: string, reasonId: string, orderRef: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_REJECT_TRANSFER_OWNERSHIP_ORDER,
            payload: Ajax.post(
                {
                    url: API_URL_REJECT_TRANSFER_OWNERSHIP_ORDER,
                    data: { reasonText, reasonId, orderRef },
                },
                dispatch,
            ),
        });

export const sendSMSOrSubmit =
    (phoneNumber: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const onlyDigits = phoneNumber.replace(/\D/g, "");
        return dispatch({
            type: AT.CHECKOUT_SEND_SMS,
            payload: Ajax.post(
                {
                    url: API_URL_SEND_SMS,
                    data: {
                        phoneNumber: onlyDigits,
                    },
                },
                dispatch,
            ),
        });
    };

export const setOfferingCode =
    (offeringCode: OfferingCode): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_SET_OFFERING_CODE,
            payload: Ajax.post(
                {
                    url: API_URL_SET_OFFERING_CODE,
                    data: {
                        offeringCode,
                    },
                },
                dispatch,
            ),
        });

export const setTransferDate =
    (transferDate: Date): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_SET_TRANSFER_DATE,
            payload: Ajax.post(
                {
                    url: API_URL_SET_TRANSFER_DATE,
                    data: {
                        transferDate,
                    },
                },
                dispatch,
            ),
        });

export const setKeepAgreement =
    (type: string, keepAgreement: boolean): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_SET_KEEP_AGREEMENT,
            payload: Ajax.post(
                {
                    url: API_URL_SET_KEEP_AGREEMENT,
                    data: {
                        type,
                        keepAgreement,
                    },
                },
                dispatch,
            ),
        });

/////////////////////////////////////////////
// From checkout existing => existing flow //
/////////////////////////////////////////////
export const submitConsent =
    (orderGuid: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_SUBMIT_TRANSFER_OWNERSHIP_CONSENT,
            payload: Ajax.post(
                {
                    url: API_URL_SUBMIT_CONSENT,
                    data: { orderGuid },
                },
                dispatch,
            ),
        });

export const rejectConsent =
    (orderGuid: string, reasons: string[]): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_REJECT_TRANSFER_OWNERSHIP_CONSENT,
            payload: Ajax.post(
                {
                    url: API_URL_REJECT_CONSENT,
                    data: { orderGuid, reasons },
                },
                dispatch,
            ),
        });

//////////////////////////
// To B2B specific flow //
//////////////////////////
export const rejectTransferOwnershipOrderToB2B =
    (reasonText: string, reasonId: string, orderItemGuid: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_REJECT_TRANSFER_OWNERSHIP_ORDER,
            payload: Ajax.post(
                {
                    url: API_URL_REJECT_TRANSFER_OWNERSHIP_ORDER_TO_B2B,
                    data: { reasonText, reasonId, orderItemGuid },
                },
                dispatch,
            ),
        });

export const submitTransferOwnershipOrderToB2B = (
    orderItemGuid: string,
    orgName: string,
    orgNumber: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    const organizationData = orgName && orgNumber ? { name: orgName, number: orgNumber } : undefined;

    return (dispatch) => {
        return dispatch({
            type: AT.CHECKOUT_SUBMIT_TRANSFER_OWNERSHIP_ORDER,
            payload: Ajax.post(
                {
                    url: API_URL_SUBMIT_TRANSFER_OWNERSHIP_ORDER_TO_B2B,
                    data: {
                        orderItemGuid,
                        organizationData,
                    },
                },
                dispatch,
            ),
        });
    };
};

export const updateOrgDataOfPortinOrTransferToB2B = (
    orderItemGuid: string,
    orgName: string,
    orgNumber: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    const organizationData = orgName && orgNumber ? { name: orgName, number: orgNumber } : undefined;

    return (dispatch) => {
        return dispatch({
            type: AT.CHECKOUT_UPDATE_ORG_CONSENT_TO_B2B,
            payload: Ajax.post(
                {
                    url: API_URL_UPDATE_ORG_CONSENT_TO_B2B,
                    data: {
                        orderItemGuid,
                        organizationData,
                    },
                },
                dispatch,
            ),
        });
    };
};

export const updateFormerLegalB2B = (
    orderItemGuid: string,
    orgName: string,
    orgNumber: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    const organizationData = orgName && orgNumber ? { name: orgName, number: orgNumber } : undefined;

    return (dispatch) => {
        return dispatch({
            type: AT.CHECKOUT_UPDATE_FORMER_LEGAL_B2B,
            meta: { organizationData },
            payload: Ajax.post(
                {
                    url: API_URL_UPDATE_FORMER_LEGAL_B2B,
                    data: {
                        orderItemGuid,
                        organizationData,
                    },
                },
                dispatch,
            ),
        });
    };
};

export const bankIdResetWhenConsentToB2B = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) =>
    dispatch({
        type: AT.CONSENT_TO_B2B_BANKID_RESET,
        payload: Ajax.post({ url: API_URL_CONSENT_TO_B2B_BANKID_RESET }, dispatch),
    });

/////////////////////////////////
// PortIn Failed specific flow //
/////////////////////////////////
export const sendNumberOrEmail = ({
    number,
    orderGuid,
}: {
    number: string;
    orderGuid: string;
}): ThunkAction<void, RootState, unknown, AnyAction> => {
    return (dispatch) => {
        dispatch({
            type: AT.CHECKOUT_SUBMIT_PORTIN_DATA,
            payload: Ajax.post(
                {
                    url: API_URL_SMS_OR_EMAIL,
                    data: { phoneNumber: number, orderGuid: orderGuid },
                },
                dispatch,
            ),
        });
    };
};

export const submitPortinTransfer =
    (orderGuid: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_SUBMIT_PORTIN_TRANSFER_OWNERSHIP_ORDER,
            payload: Ajax.post(
                {
                    url: API_URL_SUBMIT_PORTIN_TRANSFER_OWNERSHIP_ORDER,
                    data: { orderGuid },
                },
                dispatch,
            ),
        });

export const rejectPortinTransfer =
    (orderGuid: string, reasons: string[]): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_REJECT_PORTIN_TRANSFER_OWNERSHIP_ORDER,
            payload: Ajax.post(
                {
                    url: API_URL_REJECT_PORTIN_TRANSFER_OWNERSHIP_ORDER,
                    data: { orderGuid, reasons },
                },
                dispatch,
            ),
        });

export const rejectFromFormerLegal = (
    orderRef: string,
    reasonText: string,
    reasonId: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return (dispatch) => {
        return dispatch({
            type: AT.TRANSFER_REJECT_FROM_FORMER_LEGAL,
            payload: Ajax.post(
                {
                    url: API_URL_FORMER_LEGAL_CONSENT_REJECT,
                    data: { orderRef, reasonText, reasonId },
                },
                dispatch,
            ),
        });
    };
};

export const resetVerificationFromFormerLegal = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) =>
    dispatch({
        type: AT.TRANSFER_RESET_VERIFICATION_FROM_FORMER_LEGAL,
        payload: Ajax.post({ url: API_URL_CONSENT_TO_B2B_BANKID_RESET }, dispatch),
    });

export const sendCodeFromFormerLegal =
    (phoneNumber: string): ThunkAction<unknown, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.TRANSFER_SEND_CODE_FROM_FORMER_LEGAL,
            payload: Ajax.post(
                { url: API_URL_FORMER_LEGAL_CONSENT_SEND_CODE, data: { phoneNumber: phoneNumber.replace(/\s/g, "") } },
                dispatch,
            ),
        });

export const verifyCodeFromFormerLegal =
    (phoneNumber: string, pin: string): ThunkAction<unknown, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.TRANSFER_VERIFY_CODE_FROM_FORMER_LEGAL,
            payload: Ajax.post(
                {
                    url: API_URL_FORMER_LEGAL_CONSENT_VERIFY_CODE,
                    data: { phoneNumber: phoneNumber.replace(/\s/g, ""), pin },
                },
                dispatch,
            ),
        });

export const approvalFromFormerLegal = (orderRef: string): ThunkAction<void, RootState, unknown, AnyAction> => {
    return (dispatch) => {
        return dispatch({
            type: AT.TRANSFER_APPROVE_FROM_FORMER_LEGAL,
            payload: Ajax.post(
                {
                    url: API_URL_FORMER_LEGAL_CONSENT_APPROVE,
                    data: { orderRef },
                },
                dispatch,
            ),
        });
    };
};
